import React, { useEffect, useRef, useState } from "react";
import { FaMapMarkerAlt, FaCoffee, FaExclamationTriangle } from "react-icons/fa";
import api from "../../services/api";

const LocationTracker = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const token = localStorage.getItem("token");
  const trackingInterval = useRef(null);
  const idleCheckInterval = useRef(null);
  
  // GPS status tracking
  const gpsWatchInterval = useRef(null);
  const gpsOffStartTime = useRef(null);
  
  const [trackerStatus, setTrackerStatus] = useState({
    status: "initializing",
    message: "Starting location tracking...",
    isInside: true,
    distance: 0,
    lat: null,
    lng: null,
    accuracy: null,
    lastUpdate: null,
    gpsEnabled: true,
  });
  const [isCheckedOut, setIsCheckedOut] = useState(false);
  const [isOutside, setIsOutside] = useState(false);
  const [isActivelyTracking, setIsActivelyTracking] = useState(false);
  
  const [isGpsOff, setIsGpsOff] = useState(false);

  const isBreakTime = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const totalMinutes = (hours * 60) + minutes;
    const breakStart = (13 * 60) + 30;
    const breakEnd = (14 * 60) + 0;
    return totalMinutes >= breakStart && totalMinutes <= breakEnd;
  };

  const isOutsideWorkingHours = () => {
    const now = new Date();
    const hours = now.getHours();
    return hours < 9 || hours >= 17;
  };

  const stopAllIntervals = () => {
    if (trackingInterval.current) {
      clearInterval(trackingInterval.current);
      trackingInterval.current = null;
    }
    if (idleCheckInterval.current) {
      clearInterval(idleCheckInterval.current);
      idleCheckInterval.current = null;
    }
    if (gpsWatchInterval.current) {
      clearInterval(gpsWatchInterval.current);
      gpsWatchInterval.current = null;
    }
    setIsActivelyTracking(false);
  };

  const calculateDistanceFromCoords = (lat1, lng1, lat2, lng2) => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return Math.round(R * c);
  };

  // ✅ NEW: Check if GPS/location is enabled
  const checkGpsStatus = () => {
    if (!navigator.permissions) {
      // Fallback: Try getting location to check
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          () => {
            // GPS is ON
            handleGpsTurnedOn();
          },
          (error) => {
            if (error.code === error.PERMISSION_DENIED || 
                error.code === error.POSITION_UNAVAILABLE) {
              // GPS is OFF
              handleGpsTurnedOff();
            }
          },
          { timeout: 5000, maximumAge: 10000 }
        );
      }
      return;
    }

    navigator.permissions.query({ name: 'geolocation' })
      .then((result) => {
        if (result.state === 'granted') {
          handleGpsTurnedOn();
        } else {
          handleGpsTurnedOff();
        }
        
        // Listen for changes
        result.onchange = () => {
          if (result.state === 'granted') {
            handleGpsTurnedOn();
          } else {
            handleGpsTurnedOff();
          }
        };
      })
      .catch(() => {
        // Permissions API not supported, fallback to geolocation check
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            () => handleGpsTurnedOn(),
            (error) => {
              if (error.code === error.PERMISSION_DENIED || 
                  error.code === error.POSITION_UNAVAILABLE) {
                handleGpsTurnedOff();
              }
            },
            { timeout: 5000, maximumAge: 10000 }
          );
        }
      });
  };

  // ✅ NEW: Handle GPS turned OFF
  const handleGpsTurnedOff = () => {
    if (isGpsOff) return; // Already handling GPS off
    
    console.log("GPS turned OFF - Starting outside time tracking");
    setIsGpsOff(true);
    gpsOffStartTime.current = new Date();
    
    // Stop location tracking intervals
    if (trackingInterval.current) {
      clearInterval(trackingInterval.current);
      trackingInterval.current = null;
    }
    if (idleCheckInterval.current) {
      clearInterval(idleCheckInterval.current);
      idleCheckInterval.current = null;
    }
    setIsActivelyTracking(false);
    
    // Update UI
    setTrackerStatus(prev => ({
      ...prev,
      gpsEnabled: false,
      status: "gps_off",
      message: "GPS is OFF - Counting as outside time",
      isInside: false,
    }));
    
    // Send GPS OFF event to server
    sendGpsOffToServer();
  };

  // ✅ NEW: Handle GPS turned ON
  const handleGpsTurnedOn = () => {
    if (!isGpsOff) return; // GPS was not off
    
    console.log("GPS turned ON - Resuming normal tracking");
    
    const gpsOffDuration = gpsOffStartTime.current 
      ? Math.floor((new Date() - gpsOffStartTime.current) / 1000)
      : 0;
    
    setIsGpsOff(false);
    gpsOffStartTime.current = null;
    
    // Update UI
    setTrackerStatus(prev => ({
      ...prev,
      gpsEnabled: true,
      status: "active",
      message: "GPS is ON - Tracking resumed",
    }));
    
    // Send GPS ON event with duration to server
    sendGpsOnToServer(gpsOffDuration);
    
    // Resume normal tracking
    idleLocationCheck();
    if (!idleCheckInterval.current) {
      idleCheckInterval.current = setInterval(idleLocationCheck, 300000);
    }
  };

  // ✅ NEW: Send GPS OFF event to server
  const sendGpsOffToServer = async () => {
    try {
      const response = await api.post("/attendance/gps_status", 
        { 
          gps_enabled: false,
          event_time: new Date().toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("GPS OFF sent to server:", response.data);
    } catch (error) {
      console.error("Failed to send GPS OFF:", error.message);
    }
  };

  // ✅ NEW: Send GPS ON event with duration to server
  const sendGpsOnToServer = async (gpsOffDurationSeconds) => {
    try {
      const response = await api.post("/attendance/gps_status", 
        { 
          gps_enabled: true,
          event_time: new Date().toISOString(),
          gps_off_duration_seconds: gpsOffDurationSeconds
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("GPS ON sent to server:", response.data);
    } catch (error) {
      console.error("Failed to send GPS ON:", error.message);
    }
  };

  // ✅ Send location to server
  const sendLocationToServer = async (lat, lng, accuracy) => {
    try {
      const response = await api.post("/attendance/track_location", 
        { lat, lng, accuracy },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = response.data;
      
      if (data.status === "already_checked_out" || data.status === "no_active_attendance") {
        setIsCheckedOut(true);
        stopAllIntervals();
        return null;
      }

      setTrackerStatus(prev => ({
        ...prev,
        status: data.status,
        message: data.message || "",
        isInside: data.is_inside,
        distance: data.distance || prev.distance,
        pauseMinutes: data.total_pause_minutes || data.current_pause_minutes || 0
      }));

      return data;
    } catch (error) {
      console.error("Track location error:", error.message);
      return null;
    }
  };

  // ✅ Called every 5 minutes - to detect if user left or came back
  const idleLocationCheck = async () => {
    if (isCheckedOut || isOutsideWorkingHours() || isBreakTime()) return;
    if (isGpsOff) return; // ✅ Don't check if GPS is off

    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const officeLat = 30.661770978901735;
        const officeLng = 73.13606289934688;
        const calculatedDistance = calculateDistanceFromCoords(lat, lng, officeLat, officeLng);
        const isInsideLocal = calculatedDistance <= 180;

        setTrackerStatus(prev => ({
          ...prev,
          lat, lng,
          accuracy: position.coords.accuracy,
          distance: calculatedDistance,
          isInside: isInsideLocal,
          lastUpdate: new Date().toLocaleTimeString(),
          gpsEnabled: true
        }));

        const serverResponse = await sendLocationToServer(lat, lng, position.coords.accuracy);
        
        if (!serverResponse) return;

        if (!isInsideLocal && !isOutside) {
          console.log("🚶 User left office - Starting active tracking");
          setIsOutside(true);
          startActiveTracking();
        } else if (isInsideLocal && isOutside) {
          console.log("🏢 User returned to office - Stopping active tracking, Timer resumed");
          setIsOutside(false);
          stopActiveTracking();
        }
      },
      (error) => {
        console.log("Idle GPS error:", error.message);
        
        // ✅ NEW: If permission denied, treat as GPS OFF
        if (error.code === error.PERMISSION_DENIED) {
          handleGpsTurnedOff();
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  // ✅ Called every 60 seconds ONLY when user is outside
  const activeLocationTrack = async () => {
    if (isCheckedOut) {
      stopAllIntervals();
      return;
    }
    if (isGpsOff) return; // ✅ Don't track if GPS is off

    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const officeLat = 30.661770978901735;
        const officeLng = 73.13606289934688;
        const calculatedDistance = calculateDistanceFromCoords(lat, lng, officeLat, officeLng);
        const isInsideLocal = calculatedDistance <= 180;

        setTrackerStatus(prev => ({
          ...prev,
          lat, lng,
          accuracy: position.coords.accuracy,
          distance: calculatedDistance,
          isInside: isInsideLocal,
          lastUpdate: new Date().toLocaleTimeString(),
          gpsEnabled: true
        }));

        const serverResponse = await sendLocationToServer(lat, lng, position.coords.accuracy);
        
        if (!serverResponse) return;

        if (isInsideLocal) {
          console.log("🏢 User returned - Stopping active tracking, Timer resumed");
          setIsOutside(false);
          stopActiveTracking();
        }
      },
      (error) => {
        console.log("Active GPS error:", error.message);
        
        // ✅ NEW: If permission denied, treat as GPS OFF
        if (error.code === error.PERMISSION_DENIED) {
          handleGpsTurnedOff();
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const startActiveTracking = () => {
    if (trackingInterval.current) return;
    setIsActivelyTracking(true);
    console.log("Active tracking started (every 60s)");
    activeLocationTrack();
    trackingInterval.current = setInterval(activeLocationTrack, 60000);
  };

  const stopActiveTracking = () => {
    if (trackingInterval.current) {
      clearInterval(trackingInterval.current);
      trackingInterval.current = null;
    }
    setIsActivelyTracking(false);
    console.log("Active tracking stopped - Timer should be running now");
  };

  useEffect(() => {
    if (!user.id || user.role === 'admin') return;

    console.log("LocationTracker started with GPS monitoring");

    // ✅ NEW: Start GPS status checking
    checkGpsStatus();
    gpsWatchInterval.current = setInterval(checkGpsStatus, 30000); // Check every 30 seconds

    // Initial location check
    const initialTimeout = setTimeout(() => {
      idleLocationCheck();
    }, 3000);

    // Idle check every 5 minutes
    idleCheckInterval.current = setInterval(idleLocationCheck, 300000);

    return () => {
      clearTimeout(initialTimeout);
      stopAllIntervals();
    };
  }, [user.id, token, isCheckedOut]);

  // ✅ Hide only if checked out or outside working hours
  if (isCheckedOut || isOutsideWorkingHours()) {
    return null;
  }

  // ✅ Show badge with GPS status
  return (
    <div style={{
      position: 'fixed',
      bottom: '10px',
      right: '10px',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      alignItems: 'flex-end'
    }}>
      <div style={{
        background: isBreakTime() ? '#f59e0b' : 
                    isGpsOff ? '#dc2626' :
                    trackerStatus.isInside ? '#10b981' : '#ef4444',
        color: 'white',
        padding: '10px 16px',
        borderRadius: '24px',
        fontSize: '13px',
        fontWeight: '600',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
        opacity: 0.95,
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }} title={trackerStatus.message}>
        <span style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          background: isBreakTime() ? '#fff' :
                      isGpsOff ? '#fca5a5' :
                      trackerStatus.isInside ? '#fff' : '#fca5a5',
          animation: (!trackerStatus.isInside || isGpsOff) ? 'pulse 1.5s infinite' : 'none'
        }}></span>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            {isBreakTime() ? <><FaCoffee style={{ color: '#fff' }} /> Break Time</> :
             isGpsOff ? <><FaExclamationTriangle style={{ color: '#fff' }} /> GPS OFF - Outside Time</> :
             trackerStatus.isInside ? <><FaMapMarkerAlt style={{ color: '#fff' }} /> In Office</> : <><FaMapMarkerAlt style={{ color: '#fff' }} /> Outside Office</>}
          </span>
          <span style={{ fontSize: '11px', opacity: 0.8 }}>
            {isBreakTime() ? 'Tracking paused' :
             isGpsOff ? 'GPS disabled - counting outside' :
             trackerStatus.distance > 0 ? `${trackerStatus.distance}m away` : ''}
          </span>
        </div>
      </div>
    </div>
  );
};

export default LocationTracker;